import { useCallback, useState } from "react";
import { Dices, Shuffle, X, Loader2 } from "lucide-react";
import { api, fmtDate, displayPlatforms } from "../../lib/api";
import { VoteButtons } from "./VoteButtons";
import { useReleaseModal } from "./ReleaseModal";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

// "Choisis pour moi" — random release picker. Backend enforces the quality
// constraints (released, has poster, has platform); we only take care of
// the UX (open, roll, re-roll, kind filter, vote).
export const RandomPicker = () => {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("all"); // all | Film | Série
  const [error, setError] = useState(null);

  const roll = useCallback(
    async (k = kind) => {
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (k !== "all") params.kind = k;
        // Cache-buster on top of `$sample` to avoid any CDN edge caching.
        params._ = Date.now();
        const { data } = await api.get("/releases/random", { params });
        setPick(data);
      } catch (e) {
        setPick(null);
        setError(
          e?.response?.status === 404
            ? "Aucun contenu disponible pour ce filtre."
            : "Impossible de piocher pour l'instant, réessaie dans un instant."
        );
      } finally {
        setLoading(false);
      }
    },
    [kind]
  );

  const openAndRoll = () => {
    // Consistent "modale vide à chaque clic" UX — reset state on open.
    setPick(null);
    setError(null);
    setKind("all");
    setOpen(true);
    roll("all");
  };

  const setKindAndRoll = (k) => {
    setKind(k);
    roll(k);
  };

  const posterUrl = pick?.poster_url || "";
  const { openRelease } = useReleaseModal();
  const showDetails = () => {
    setOpen(false);
    openRelease(pick);
  };
  const year = pick?.release_date ? pick.release_date.slice(0, 4) : "";

  return (
    <>
      <button
        type="button"
        onClick={openAndRoll}
        data-testid="random-picker-btn"
        className="group btn-ghost"
      >
        <Dices className="h-4 w-4 group-hover:rotate-12 transition-transform" />
        Choisis pour moi
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="random-picker-modal"
          className="max-w-[720px] p-0 overflow-hidden bg-white"
        >
          <DialogTitle className="sr-only">Choisis pour moi</DialogTitle>
          <DialogDescription className="sr-only">
            Un film ou une série tirés au sort parmi les contenus disponibles.
          </DialogDescription>

          {/* Header — kind toggle + reroll */}
          <div className="flex items-center justify-between gap-3 px-5 md:px-6 pt-5 pb-3 border-b border-[var(--line)]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[var(--red)]">
              <Dices className="h-3.5 w-3.5" />
              Choisis pour moi
            </div>
            <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1 text-[10px] uppercase tracking-widest">
              {[
                ["all", "Tout"],
                ["Film", "Films"],
                ["Série", "Séries"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`random-picker-kind-${k}`}
                  onClick={() => setKindAndRoll(k)}
                  className={`px-3 py-1.5 rounded-full transition-colors ${
                    kind === k
                      ? "bg-black text-white"
                      : "text-neutral-500 hover:text-black"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="px-5 md:px-6 py-6">
            {loading ? (
              <div className="min-h-[300px] flex flex-col items-center justify-center gap-3 text-neutral-400">
                <Loader2 className="h-6 w-6 animate-spin text-[var(--red)]" />
                <div className="text-xs uppercase tracking-widest">
                  La roulette tourne…
                </div>
              </div>
            ) : error ? (
              <div className="min-h-[300px] flex flex-col items-center justify-center gap-2 text-center">
                <div className="text-sm text-neutral-500">{error}</div>
              </div>
            ) : pick ? (
              <div
                key={pick.tmdb_id + "-" + pick.type}
                data-testid="random-picker-result"
                className="grid md:grid-cols-[200px_1fr] gap-6 animate-in fade-in zoom-in-95 duration-300"
              >
                <button
                  type="button"
                  onClick={showDetails}
                  data-testid="random-picker-poster-btn"
                  aria-label={`Voir le détail de ${pick.title}`}
                  className="w-[200px] h-[300px] mx-auto md:mx-0 bg-neutral-100 rounded overflow-hidden border border-[var(--line)] cursor-pointer hover:opacity-90 transition-opacity"
                >
                  {posterUrl ? (
                    <img
                      src={posterUrl}
                      alt={pick.title}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </button>
                <div className="flex flex-col">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)]">
                    {pick.type} {year ? `· ${year}` : ""}
                  </div>
                  <h2
                    data-testid="random-picker-title"
                    className="font-display text-2xl md:text-3xl mt-1 leading-tight"
                  >
                    {pick.title}
                  </h2>
                  {pick.platforms?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {displayPlatforms(pick).map((p) => (
                        <span
                          key={p}
                          className="text-[10px] uppercase tracking-widest bg-neutral-900 text-white px-2 py-1 rounded"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  )}
                  {pick.genres?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {pick.genres.slice(0, 4).map((g) => (
                        <span
                          key={g}
                          className="text-[10px] uppercase tracking-widest border border-[var(--line-strong)] text-neutral-600 px-2 py-1 rounded"
                        >
                          {g}
                        </span>
                      ))}
                    </div>
                  )}
                  {pick.release_date && (
                    <div className="mt-4 text-xs text-neutral-500">
                      Sortie · {fmtDate(pick.release_date)}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={showDetails}
                    data-testid="random-picker-details-btn"
                    className="mt-3 self-start text-xs uppercase tracking-[0.2em] text-[var(--red)] hover:underline underline-offset-4"
                  >
                    Voir le synopsis →
                  </button>
                  <div className="mt-5">
                    <VoteButtons release={pick} />
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer — actions */}
          <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-t border-[var(--line)] bg-[var(--bg-cream)]">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 h-9 px-4 text-xs uppercase tracking-widest text-neutral-500 hover:text-black"
            >
              <X className="h-3.5 w-3.5" /> Fermer
            </button>
            <button
              type="button"
              onClick={() => roll()}
              data-testid="random-picker-reroll"
              disabled={loading}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-[var(--red)] text-white text-xs uppercase tracking-[0.2em] hover:bg-black transition-colors disabled:opacity-50"
            >
              <Shuffle className="h-3.5 w-3.5" />
              Piocher un autre
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
